import React, { Component } from 'react';
import { injectIntl, intlShape } from 'react-intl';
import { connect } from 'react-redux';
import ReactHighcharts from 'react-highcharts';
import { unix } from 'moment';

const chartConfig = {
  title: {
    text: null,
  },

  xAxis: {
    categories: [],
    lineColor: '#eee',
    crosshair: true,
    tickColor: 'transparent',
    labels: {
      style: {
        fontFamily: 'Montserrat, "Helvetica Neue", Helvetica, Arial, sans-serif',
        color: '#555',
        fontSize: 14,
        lineHeight: '21px',
      },
    },
  },

  yAxis: {
    gridLineColor: 'transparent',
    min: 0,
    title: {
      text: null,
    },
    labels: {
      enabled: false,
    },
    opposite: true,
  },

  credits: {
    text: '',
    href: '',
  },

  legend: {
    enabled: false,
  },
  tooltip: {
    shared: true,
  },
  plotOptions: {
    column: {
      grouping: false,
      shadow: false,
      borderWidth: 0,
      states: {
        hover: {
          enabled: false,
        },
      },
    },
  },

  symbols: ['circle'],
  tooltip: {
    backgroundColor: '#222',
    borderWidth: 0,
    borderRadius: 8,
    padding: 8,
    shadow: false,
    crosshairs: false,
    shared: true,
    useHTML: true,
    style: {
      color: '#fff',
      cursor: 'default',
      fontFamily: 'Montserrat, "Helvetica Neue", Helvetica, Arial, sans-serif',
      fontSize: 12,
      lineHeight: '16px',
      fontWeight: 700,
      textAlign: 'center',
      whiteSpace: 'nowrap',
    },
  },
};

const renderTooltipPluralizeLabel = ({ intl, id, values }) =>
  intl.formatMessage({ id }, { values });

const getFormattedTooltip = (data = {}, type = 'times', timezone, intl) => {
  let hours;
  let tooltip;
  const formatted = unix(data.x).tz(timezone);

  if (type === 'hourly') {
    let hour = data.x > 9 ? data.x : `0${data.x}`;
    let nextHour = data.x + 1;
    nextHour = nextHour > 9 ? nextHour : `0${nextHour}`;
    hours = `${hour}:00 &ndash; ${nextHour}:00`;
  }

  if (data.points.length > 1) {
    const [clicks, leads] = data.points;

    tooltip = `<div class="statitics-tooltip">
                <span class="statitics-tooltip-header">
                  ${type === 'hourly' ? '' : formatted.format('DD MMM')}
                </span>

                ${type === 'hourly' ? `<span class="statitics-tooltip-sub">${hours}</span>` : ''}

                <ul class="statitics-tooltip-values list-unstyled no-margin">
                  <li>
                    <span class="statitics-tooltip-color" style="color:${
                      clicks.color
                    }">\u25CF</span>
                     ${renderTooltipPluralizeLabel({
                       intl,
                       id: 'widgets.statistics.tooltip.clicks',
                       values: clicks.y,
                     })}
                  </li>
                  <li>
                    <span class="statitics-tooltip-color" style="color:${leads.color}">\u25CF</span>
                     ${renderTooltipPluralizeLabel({
                       intl,
                       id: 'widgets.statistics.tooltip.leads',
                       values: leads.y,
                     })}
                  </li>
                </ul>
              </div>`;
  } else if (data.points.length === 1 && indicator) {
    const [points] = data.points;
    const label = indicator.id;

    tooltip = `<div class="statitics-tooltip">
              <span class="statitics-tooltip-header">${
                type === 'hourly' ? '' : formatted.format('DD MMM')
              }</span>
              ${type === 'hourly' ? `<span class="statitics-tooltip-sub">${hours}</span>` : ''}
              <ul class="statitics-tooltip-values list-unstyled no-margin">
                <li>
                  <span class="statitics-tooltip-color" style="color:${points.color}">\u25CF</span>
                  ${points.y} ${label}
                </li>
              </ul>
            </div>`;
  }

  return tooltip;
};

class StatisticsTableChart extends Component {
  static propTypes = {
    intl: intlShape.isRequired,
  };

  constructor(props) {
    super(props);

    this.state = {
      loading: false,
      data: {
        series: [],
        tooltip: {
          ...chartConfig.tooltip,
        },
        xAxis: {
          ...chartConfig.xAxis,
        },
        chart: {
          type: this.props.theme && this.props.theme === 2 ? 'line' : 'column',
          height: 248,
          zoomType: 'x',
        },
      },
    };
  }

  componentDidMount() {
    const { intl, stats } = this.props;

    ReactHighcharts.Highcharts.setOptions({
      lang: {
        resetZoom: intl.formatMessage({
          id: 'chart.buttons.reset_zoom',
          defaultMessage: 'Reset zoom',
        }),
      },
    });

    stats && stats.length && this.formatSeries(stats);
  }

  componentDidUpdate(prevProps) {
    // проверяем длину статы, валюту, количество кликов и лидов
    // если не что-то не совпадает - перерисовываем график
    if (
      this.props.stats &&
      (this.props.stats.length != prevProps.stats.length ||
        prevProps.currency !== this.props.currency ||
        prevProps.total['24'] !== this.props.total['24'] ||
        prevProps.total['127'] !== this.props.total['127']) 
    ) {
      this.formatSeries(this.props.stats);
    }

    if (this.props.theme !== prevProps.theme) {
      this.changeChartType(this.props);
    }
  }

  changeChartType = nextProps => {
    this.setState(prevState => ({
      data: {
        ...prevState.data,
        chart: {
          ...prevState.data.chart,
          type: nextProps.theme === 2 ? 'line' : 'column',
        },
      },
    }));
  };

  getChartConfig = () => {
    const config = {
      ...chartConfig,
      ...this.state.data,
    };
    return config;
  };

  formatSeries = (data = []) => {
    const { timezone, theme, intl, currency, type } = this.props;

    const series = [
      {
        name: 'Clicks',
        data: [],
        color: theme === 2 ? '#3db5e6' : '#f5f5fa',
        pointPadding: 0,
        groupPadding: 0,
        borderWidth: 1,
      },
      {
        name: 'Leads',
        data: [],
        color: theme === 2 ? '#9579d3' : '#ffee00',
        pointPadding: 0,
        groupPadding: 0,
        borderWidth: 1,
      },
    ];

    const categories = [];
    const format = type === 'hourly' ? 'HH:mm' : 'DD MMM';
    const categoryIndex = type === 'hourly' ? '4' : '3';
    data.length > 0 &&
      data.forEach(item => {
        if (!item[categoryIndex]) return;
        categories.push(item[categoryIndex][currency]);
        series[0].data.push(item['24'][currency]);
        series[1].data.push(item['127'][currency]);
      });

    this.setState(prevState => ({
      data: {
        ...prevState.data,
        series,
        tooltip: {
          ...prevState.data.tooltip,
          formatter: function() {
            return getFormattedTooltip(this, type, timezone, intl);
          },
        },

        xAxis: {
          ...prevState.data.xAxis,
          categories,
          labels: {
            ...prevState.data.xAxis.labels,
            formatter: function() {
              // в стате по часам приходят часы, а не ts
              if (this.value > 23) {
                return unix(this.value)
                  .tz(timezone)
                  .format(format);
              } else {
                const val = this.value > 9 ? this.value : `0${this.value}`;
                return `${val}:00`;
              }
            },
          },
        },
      },
    }));
  };

  render() {
    return <ReactHighcharts ref="chart" config={this.getChartConfig()} />;
  }
}

const mapStateToProps = ({ webmaster }) => ({
  timezone: webmaster.profile.timezone,
  theme: webmaster.profile.theme,
});

export default connect(mapStateToProps)(injectIntl(StatisticsTableChart));
